import { auth } from '../Firebase';

// Environment-aware URLs
const isDevelopment = window.location.hostname === 'localhost';
const devHostingUrl =
  import.meta.env.VITE_HOSTING_URL || 'http://localhost:5002';

const createSessionUrl = isDevelopment
  ? `${devHostingUrl}/createSession`
  : 'https://internal-api.superfunnel.ai/createSession';

interface SessionResponse {
  ok: boolean;
  sessionCookie?: string;
  devNote?: string;
}

class SessionManager {
  private sessionExpiryTime: number | null = null;
  private createSessionPromise: Promise<void> | null = null;

  /**
   * Creates or refreshes the session cookie
   * This should be called after successful login and before making authenticated requests
   * @param {boolean} forceRefresh - Whether to force refresh even if session appears valid
   */
  async createSession(forceRefresh = false): Promise<void> {
    // If there's already a session creation in progress, wait for it
    if (this.createSessionPromise && !forceRefresh) {
      return this.createSessionPromise;
    }

    // Check if we have a valid session (not expired)
    if (!forceRefresh && this.isSessionValid()) {
      return;
    }

    // Create a new session creation promise
    this.createSessionPromise = this._createSessionInternal();

    try {
      await this.createSessionPromise;
    } finally {
      this.createSessionPromise = null;
    }
  }

  /**
   * Internal method to create the session
   */
  private async _createSessionInternal(): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to create session');
    }

    const idToken = await currentUser.getIdToken(/* forceRefresh */ true);

    const response = await fetch(createSessionUrl, {
      method: 'POST',
      credentials: isDevelopment ? undefined : 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken, remember: true }),
    });

    if (!response.ok) {
      throw new Error('Failed to create session');
    }

    const sessionData: SessionResponse = await response.json();

    // In development mode, manually set the session cookie
    if (sessionData.sessionCookie) {
      // Set the cookie manually for development
      document.cookie = `session=${sessionData.sessionCookie}; Path=/; SameSite=None; Secure`;
    }

    // Set expiry time (5 days from now, matching the server-side logic)
    this.sessionExpiryTime = Date.now() + 5 * 24 * 60 * 60 * 1000;
  }

  /**
   * Checks if the current session is still valid
   * @return {boolean} True if session is valid, false otherwise
   */
  private isSessionValid(): boolean {
    if (!this.sessionExpiryTime) {
      return false;
    }

    // Add a 5-minute buffer before expiry to ensure we refresh before it expires
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    return Date.now() < this.sessionExpiryTime - bufferTime;
  }

  /**
   * Ensures a valid session exists, creating one if necessary
   * This is the main method that should be called before making authenticated requests
   */
  async ensureValidSession(): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    if (!this.isSessionValid()) {
      await this.createSession(true);
    }
  }

  /**
   * Clears the session data (useful for logout)
   */
  clearSession(): void {
    this.sessionExpiryTime = null;
    this.createSessionPromise = null;

    // Clear the session cookie
    if (isDevelopment) {
      document.cookie =
        'session=; Path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }
  }
}

// Export a singleton instance
export const sessionManager = new SessionManager();

// Export the class for testing or multiple instances if needed
export { SessionManager };
